/**
 * explanations.js
 *
 * Converts triage output into human-readable result screen content.
 *
 * API:
 *   getResultContent(protocolId, careLevel, reasonCodes, answers)
 *   → { explanation: string, selfCare: string[] | null, safetyNet: string | null }
 *
 * explanation  — 1–2 sentences explaining why this care level, using answer
 *                values where available (temp, duration, worsening, etc.)
 * selfCare     — 2–4 actionable tips; present for home_care and care_soon only
 * safetyNet    — quiet "if things get worse" note; null for emergency_now
 */

// ─── Fallback explanations by care level ─────────────────────────────────────

const FALLBACK = {
  emergency_now: 'Your symptoms include a warning sign that needs emergency care right now.',
  care_today: 'Based on your answers, you should be seen by a provider today.',
  care_soon: 'Your symptoms haven\'t resolved on their own — a provider visit in the next day or two makes sense.',
  home_care: 'Based on your answers, you can manage this at home. Rest, stay hydrated, and monitor.',
};

// ─── Protocol explanation functions ──────────────────────────────────────────
// Each fn(careLevel, answers, reasonCodes) → string

function fever(level, a) {
  const temp = a.temp_value ? `${a.temp_value}°F` : null;
  const days = a.duration_days != null ? `${a.duration_days} ${a.duration_days === 1 ? 'day' : 'days'}` : null;
  const worse = a.worsening === 'worse';

  if (level === 'emergency_now') return FALLBACK.emergency_now;
  if (level === 'care_today') {
    if (temp && days && worse) return `Your ${temp} fever has lasted ${days} and is getting worse — that combination needs to be seen today.`;
    if (temp && worse)         return `A ${temp} fever that's getting worse needs attention today.`;
    if (temp && days)          return `A ${temp} fever lasting ${days} warrants being evaluated today.`;
    if (days)                  return `Your fever has lasted ${days} without clearing — time to have it looked at.`;
    return 'Your fever is high or persistent enough to need attention today.';
  }
  if (level === 'care_soon') {
    if (temp && days) return `Your ${temp} fever has been around for ${days} — a quick visit will help determine what's going on.`;
    if (days)         return `A fever lasting ${days} is worth having checked in the next day or two.`;
    return 'Your fever hasn\'t resolved on its own — a provider visit soon makes sense.';
  }
  if (level === 'home_care') {
    if (temp) return `Your fever of ${temp} is mild, you're staying hydrated, and things seem stable — rest and fluids should carry you through.`;
    return 'Your fever is mild and manageable at home. Rest, stay hydrated, and monitor for changes.';
  }
  return FALLBACK[level];
}

function cough_uri(level, a) {
  const days = a.duration_days != null ? `${a.duration_days} ${a.duration_days === 1 ? 'day' : 'days'}` : null;
  const worse = a.worsening === 'worse';
  const better = a.worsening === 'better';

  if (level === 'emergency_now') return 'Your breathing symptoms are serious — call 911 now.';
  if (level === 'care_today') {
    if (worse && days) return `Your symptoms have lasted ${days} and are getting worse — that's worth being seen today.`;
    return 'Your respiratory symptoms need to be evaluated today.';
  }
  if (level === 'care_soon') {
    if (days) return `A cough lasting ${days} isn't clearing on its own — a provider should take a look in the next day or two.`;
    return 'Your symptoms have lingered long enough that a provider visit makes sense.';
  }
  if (level === 'home_care') {
    if (better) return 'Good news — your symptoms are already improving. Rest and fluids will help your body finish the job.';
    if (days)   return `A ${days} cough with no fever that's not worsening is most likely viral — rest and fluids are the best medicine right now.`;
    return 'Your symptoms are mild. Rest up and keep an eye on things.';
  }
  return FALLBACK[level];
}

function sore_throat(level, a) {
  const severe = a.pain_severity_0_10 >= 7;
  const fever = a.fever_present === true;
  const swallowing = a.swallowing_difficulty === true;

  if (level === 'emergency_now') return 'Your throat symptoms include a warning sign that needs emergency care now.';
  if (level === 'care_today') {
    if (swallowing) return 'Difficulty swallowing with a sore throat needs to be evaluated today — it can indicate a more serious infection.';
    if (severe && fever) return 'A severe sore throat with fever should be seen today — strep and other infections are ruled out with a quick test.';
    return 'Your sore throat needs to be seen today.';
  }
  if (level === 'care_soon') {
    return fever
      ? 'A sore throat with fever that hasn\'t improved is worth having tested in the next day or two.'
      : 'Your sore throat has lingered — a provider visit will help figure out if treatment is needed.';
  }
  if (level === 'home_care') {
    return 'Your sore throat is mild and most likely viral. Rest, warm fluids, and throat lozenges should help.';
  }
  return FALLBACK[level];
}

function ear_pain(level, a) {
  const drainage = a.drainage === true;
  const fever = a.fever_present === true;
  const severity = a.pain_severity_0_10;

  if (level === 'emergency_now') return 'Your ear symptoms need emergency care immediately.';
  if (level === 'care_today') {
    if (drainage && fever) return 'Ear drainage with fever needs to be evaluated today — this can indicate an infection that needs treatment.';
    if (drainage)           return 'Drainage from your ear should be looked at today.';
    if (fever)              return 'Ear pain with a fever should be evaluated today.';
    return 'Your ear pain is significant enough to be seen today.';
  }
  if (level === 'care_soon') {
    return severity >= 6
      ? 'Your ear pain is noticeable and worth having checked in the next day or two.'
      : 'Your ear pain has been around long enough to have it looked at.';
  }
  if (level === 'home_care') {
    return 'Your ear discomfort is mild. Warm compresses and OTC pain relievers can help — monitor for any drainage or worsening.';
  }
  return FALLBACK[level];
}

function vomiting_diarrhea(level, a) {
  const fluids = a.keeping_fluids_down;
  const blood = a.blood_present === true;
  const days = a.duration_days != null ? `${a.duration_days} ${a.duration_days === 1 ? 'day' : 'days'}` : null;

  if (level === 'emergency_now') return 'Your symptoms include a serious warning sign — go to the ER now.';
  if (level === 'care_today') {
    if (fluids === false) return 'You\'re not able to keep fluids down — that\'s a dehydration risk that needs attention today.';
    if (blood)            return 'Blood in your stool or vomit needs to be evaluated today.';
    return 'Your symptoms have reached a point where being seen today is the right call.';
  }
  if (level === 'care_soon') {
    if (days) return `Your GI symptoms have gone on for ${days} — a provider can help figure out what's causing it.`;
    return 'Your stomach symptoms haven\'t resolved — a provider visit in the next day or two makes sense.';
  }
  if (level === 'home_care') {
    return 'Your symptoms are mild and you\'re keeping fluids down — the BRAT diet (bananas, rice, applesauce, toast) and rest should help.';
  }
  return FALLBACK[level];
}

function abdominal_pain(level, a) {
  const severity = a.pain_severity_0_10;
  const location = a.pain_location;
  const worse = a.worsening === 'worse';

  if (level === 'emergency_now') return 'Your abdominal symptoms include a warning sign that needs emergency care now.';
  if (level === 'care_today') {
    if (location === 'right_lower' || location === 'lower_right') return 'Pain in the lower right abdomen needs to be evaluated today to rule out appendicitis.';
    if (worse && severity >= 7) return `Your abdominal pain is significant (${severity}/10) and getting worse — that needs attention today.`;
    if (severity >= 7)          return `Your pain level of ${severity}/10 is high enough that you should be seen today.`;
    return 'Your abdominal pain should be evaluated today.';
  }
  if (level === 'care_soon') {
    if (severity) return `Your abdominal discomfort (${severity}/10) hasn't resolved — a provider visit in the next day or two is a good idea.`;
    return 'Your stomach pain has lingered — worth having checked soon.';
  }
  if (level === 'home_care') {
    return 'Your abdominal discomfort is mild and doesn\'t have any alarming features. Rest and light foods should help.';
  }
  return FALLBACK[level];
}

function urinary_symptoms(level, a) {
  const fever = a.fever_present === true;
  const flank = a.flank_pain === true;
  const pregnant = a.pregnant === true;

  if (level === 'emergency_now') return 'Your urinary symptoms combined with other signs need emergency care now.';
  if (level === 'care_today') {
    if (pregnant)      return 'Urinary symptoms during pregnancy need to be evaluated today.';
    if (fever && flank) return 'Urinary symptoms with fever and back pain can indicate a kidney infection — that needs attention today.';
    if (flank)         return 'Flank or back pain with urinary symptoms should be evaluated today.';
    if (fever)         return 'Urinary symptoms with fever should be seen today.';
    return 'Your urinary symptoms should be evaluated today.';
  }
  if (level === 'care_soon') {
    return 'Burning, frequency, or urgency that\'s been going on is most likely a UTI — a quick visit and urine test will confirm it.';
  }
  if (level === 'home_care') {
    return 'Your mild urinary discomfort may settle on its own, but drink plenty of water and monitor — UTIs typically need treatment.';
  }
  return FALLBACK[level];
}

function rash_skin(level, a) {
  const spreading = a.spreading === true;
  const fever = a.fever_present === true;
  if (level === 'emergency_now') return 'The combination of rash and swelling or breathing difficulty is a medical emergency.';
  if (level === 'care_today') {
    if (fever && spreading) return 'A rash that\'s spreading with fever should be evaluated today.';
    if (spreading)          return 'A rapidly spreading rash should be looked at today.';
    return 'Your rash or skin condition should be evaluated today.';
  }
  if (level === 'care_soon') {
    return 'Your skin condition is worth having looked at in the next day or two — a provider can identify it and recommend the right treatment.';
  }
  if (level === 'home_care') {
    return 'Your rash appears to be mild and localized. Keep the area clean and dry, and watch for spreading or any new symptoms.';
  }
  return FALLBACK[level];
}

function headache(level, a) {
  const severity = a.pain_severity_0_10;
  const fever = a.fever_present === true;
  const typical = a.headache_typical === true;
  const sudden = a.sudden_or_gradual === 'sudden';

  if (level === 'emergency_now') {
    if (sudden && severity >= 9) return 'A sudden, severe headache — sometimes called a thunderclap headache — is a medical emergency.';
    return 'Your headache has features that require emergency evaluation right now.';
  }
  if (level === 'care_today') {
    if (fever)              return 'A headache with fever should be evaluated today to rule out infection.';
    if (!typical && severity >= 8) return `A ${severity}/10 headache that's not typical for you warrants being seen today.`;
    return 'Your headache should be evaluated today.';
  }
  if (level === 'care_soon') {
    return `Your headache${severity ? ` (${severity}/10)` : ''} hasn't gone away on its own — a provider visit in the next day or two is a good idea.`;
  }
  if (level === 'home_care') {
    return typical
      ? 'This sounds like your typical headache pattern. OTC pain relievers, rest, and hydration should help.'
      : 'Your headache is mild and doesn\'t have any alarming features. Rest, fluids, and OTC pain relievers should help.';
  }
  return FALLBACK[level];
}

function minor_injury_sprain(level, a) {
  const weight = a.can_bear_weight;
  const deformity = a.deformity === true;
  const severity = a.pain_severity_0_10;

  if (level === 'emergency_now') return 'Your injury has features that require emergency evaluation immediately.';
  if (level === 'care_today') {
    if (deformity)        return 'A visible deformity at the injury site needs to be evaluated today — an X-ray can determine if anything is broken.';
    if (weight === false) return 'Not being able to bear weight on the injured area is a sign you should be seen today.';
    return 'Your injury should be evaluated today.';
  }
  if (level === 'care_soon') {
    if (severity >= 5) return `Your injury is causing moderate pain (${severity}/10) — a provider can check for a fracture or ligament damage.`;
    return 'Your sprain or strain should be looked at in the next day or two.';
  }
  if (level === 'home_care') {
    return 'Your injury appears mild. Follow RICE: Rest, Ice (20 min on, 20 off), Compression, and Elevation. OTC anti-inflammatories can help with swelling.';
  }
  return FALLBACK[level];
}

function chest_discomfort(level, a) {
  const severity = a.pain_severity_0_10;
  const worse = a.worsening === 'worse';
  const history = a.heart_history === true;

  if (level === 'emergency_now') return 'Chest discomfort with these features is a potential cardiac emergency — call 911 now.';
  if (level === 'care_today') {
    if (history && worse) return 'Chest discomfort that\'s worsening in someone with heart history needs evaluation today.';
    if (severity >= 7)    return `Chest discomfort at ${severity}/10 needs to be evaluated today — don't wait on this one.`;
    return 'Your chest symptoms should be evaluated today.';
  }
  if (level === 'care_soon') {
    return 'Your chest discomfort is lower risk based on your answers, but it\'s worth having checked in the next day or two.';
  }
  if (level === 'home_care') {
    return 'Your chest discomfort is mild and doesn\'t have alarming features. Avoid strenuous activity and monitor carefully.';
  }
  return FALLBACK[level];
}

function shortness_of_breath(level, a) {
  const rest = a.at_rest === true;
  const worse = a.worsening === 'worse';
  const asthma = a.asthma_history === true;

  if (level === 'emergency_now') return 'Shortness of breath with these features is a medical emergency — call 911 now.';
  if (level === 'care_today') {
    if (rest)            return 'Shortness of breath at rest is serious — you need to be evaluated today.';
    if (worse && asthma) return 'Worsening shortness of breath with your asthma history needs medical attention today.';
    return 'Your breathing symptoms should be evaluated today.';
  }
  if (level === 'care_soon') {
    return 'Your shortness of breath is mild but worth having checked in the next day or two.';
  }
  if (level === 'home_care') {
    return 'Your breathing discomfort is mild and manageable. Rest, avoid triggers, and monitor for any worsening.';
  }
  return FALLBACK[level];
}

function dizziness(level, a) {
  const ongoing = a.ongoing === true;

  if (level === 'emergency_now') return 'Your dizziness has features — fainting, weakness, or chest pain — that need emergency care now.';
  if (level === 'care_today') {
    if (ongoing) return 'Dizziness that\'s been going on continuously needs to be evaluated today.';
    return 'Your dizziness symptoms should be seen today.';
  }
  if (level === 'care_soon') {
    return 'Your dizziness is worth having checked in the next day or two — a provider can help identify the cause.';
  }
  if (level === 'home_care') {
    return 'Your dizziness appears mild. Change positions slowly, stay hydrated, and avoid driving until it fully resolves.';
  }
  return FALLBACK[level];
}

function allergic_reaction(level, a) {
  const epi = a.took_epi === true;
  const spreading = a.spreading === true;

  if (level === 'emergency_now') return 'Throat swelling, trouble breathing, or a rapidly spreading reaction is a medical emergency — call 911 now.';
  if (level === 'care_today') {
    if (epi)       return 'Even after using an EpiPen, allergic reactions can return — you need to be monitored in a medical setting today.';
    if (spreading) return 'A spreading allergic reaction needs to be evaluated today.';
    return 'Your allergic reaction symptoms should be seen today.';
  }
  if (level === 'care_soon') {
    return 'Your allergic reaction is manageable but worth having evaluated — a provider can help identify the trigger and recommend treatment.';
  }
  if (level === 'home_care') {
    return 'Your reaction appears mild and localized. Antihistamines (like Benadryl) can help with itch and swelling. Monitor for any spread.';
  }
  return FALLBACK[level];
}

function constipation(level, a) {
  const days = a.duration_days != null ? `${a.duration_days} ${a.duration_days === 1 ? 'day' : 'days'}` : null;
  const vomiting = a.vomiting === true;
  const bloated = a.distended === true;

  if (level === 'emergency_now') return 'Constipation with bloating and vomiting can signal a bowel obstruction — this needs emergency care now.';
  if (level === 'care_today') {
    if (vomiting && bloated) return 'Constipation with abdominal distension and vomiting needs to be evaluated today.';
    return 'Your constipation symptoms should be evaluated today.';
  }
  if (level === 'care_soon') {
    if (days) return `You've been constipated for ${days} — a provider can help get things moving and check for an underlying cause.`;
    return 'Your constipation has gone on long enough to have it checked out.';
  }
  if (level === 'home_care') {
    return 'Mild constipation often responds to increased water, fiber (fruits, vegetables, whole grains), and gentle movement. Give it a day or two.';
  }
  return FALLBACK[level];
}

function joint_pain_swelling(level, a) {
  const hot = a.hot_to_touch === true;
  const swollen = a.swollen === true;
  const injury = a.injury_mechanism === true || a.recent_injury === true;
  const fever = a.fever_present === true;

  if (level === 'emergency_now') return 'Your joint injury needs emergency evaluation — it may involve a fracture.';
  if (level === 'care_today') {
    if (hot && swollen && fever) return 'A hot, swollen joint with fever can indicate a joint infection — that needs to be seen today.';
    if (hot && swollen)          return 'A hot, swollen joint that came on suddenly should be evaluated today.';
    return 'Your joint symptoms should be seen today.';
  }
  if (level === 'care_soon') {
    return 'Your joint pain and swelling are worth having evaluated — a provider can help determine the cause and recommend treatment.';
  }
  if (level === 'home_care') {
    return injury
      ? 'Your joint pain appears to be a mild sprain or strain. RICE (Rest, Ice, Compression, Elevation) and OTC anti-inflammatories should help.'
      : 'Your joint discomfort is mild. Rest the area and use OTC anti-inflammatories if needed. Monitor for swelling or worsening.';
  }
  return FALLBACK[level];
}

function vaginal_symptoms(level, a) {
  const pregnant = a.pregnant === true;
  const bleeding = a.unusual_bleeding === true;
  const pelvic_pain = a.pelvic_pain === true;

  if (level === 'emergency_now') return 'Vaginal bleeding or pelvic pain during pregnancy needs emergency evaluation immediately.';
  if (level === 'care_today') {
    if (pregnant && (bleeding || pelvic_pain)) return 'These symptoms during pregnancy need to be evaluated today.';
    return 'Your vaginal symptoms should be seen by a provider today.';
  }
  if (level === 'care_soon') {
    return 'Your symptoms are consistent with a common condition — a provider visit in the next day or two will confirm what\'s going on and get you the right treatment.';
  }
  if (level === 'home_care') {
    return 'Your symptoms appear mild. If they persist beyond a few days or change, have them checked out.';
  }
  return FALLBACK[level];
}

function rectal_bleeding(level, a) {
  const large_amount = a.large_amount === true || a.blood_amount === 'large';
  const black_tarry = a.black_tarry === true;

  if (level === 'emergency_now') {
    if (black_tarry) return 'Black, tarry stools can indicate bleeding in the upper GI tract — this needs emergency care now.';
    return 'Significant rectal bleeding needs emergency evaluation immediately.';
  }
  if (level === 'care_today') {
    if (large_amount || black_tarry) return 'The amount or character of bleeding you\'re experiencing needs to be evaluated today.';
    return 'Rectal bleeding should be evaluated by a provider today.';
  }
  if (level === 'care_soon') {
    return 'Small amounts of rectal bleeding are common and often minor, but it\'s important to have the cause identified. A provider visit soon is the right move.';
  }
  if (level === 'home_care') {
    return 'Small amounts of bright red blood after a bowel movement are often from hemorrhoids or a small fissure. Monitor closely and see a provider if it continues.';
  }
  return FALLBACK[level];
}

function palpitations(level, a) {
  const duration = a.duration_minutes;
  const chest_pain = a.chest_pain === true;
  const very_fast = a.very_fast === true;

  if (level === 'emergency_now') {
    if (chest_pain) return 'Heart palpitations with chest pain need emergency care now.';
    return 'Your palpitations have features that require emergency evaluation immediately.';
  }
  if (level === 'care_today') {
    if (duration >= 20) return `Palpitations lasting ${duration} minutes or more need to be evaluated today.`;
    if (very_fast)      return 'Very rapid palpitations should be evaluated today.';
    return 'Your palpitations should be seen today.';
  }
  if (level === 'care_soon') {
    return 'Your palpitations are worth having checked — a provider can do an EKG and help identify the cause.';
  }
  if (level === 'home_care') {
    return 'Brief occasional palpitations are usually benign. Reduce caffeine, stress, and alcohol. Monitor and see a provider if they return or worsen.';
  }
  return FALLBACK[level];
}

function weakness_fatigue(level, a) {
  const sudden = a.sudden_onset === true;
  const one_sided = a.one_sided === true;
  const days = a.duration_days != null ? `${a.duration_days} ${a.duration_days === 1 ? 'day' : 'days'}` : null;

  if (level === 'emergency_now') {
    if (one_sided) return 'Sudden one-sided weakness is a warning sign for stroke — call 911 now.';
    return 'Your weakness or fatigue has features that need emergency care immediately.';
  }
  if (level === 'care_today') {
    if (sudden) return 'Fatigue or weakness that came on suddenly without an obvious cause should be evaluated today.';
    return 'Your fatigue or weakness should be seen by a provider today.';
  }
  if (level === 'care_soon') {
    if (days) return `Fatigue that's lasted ${days} without improving is worth having checked out.`;
    return 'Your fatigue has gone on long enough that a provider can help identify what\'s going on.';
  }
  if (level === 'home_care') {
    return 'Your fatigue appears to be related to an acute illness. Rest, stay hydrated, and give your body time to recover.';
  }
  return FALLBACK[level];
}

// ─── Explanation dispatcher ───────────────────────────────────────────────────

const EXPLANATION_FNS = {
  fever,
  cough_uri,
  sore_throat,
  ear_pain,
  vomiting_diarrhea,
  abdominal_pain,
  urinary_symptoms,
  rash_skin,
  headache,
  minor_injury_sprain,
  chest_discomfort,
  shortness_of_breath,
  dizziness,
  allergic_reaction,
  constipation,
  joint_pain_swelling,
  vaginal_symptoms,
  rectal_bleeding,
  palpitations,
  weakness_fatigue,
};

// ─── Self-care tips ───────────────────────────────────────────────────────────
// Shown for home_care and care_soon only.

const SELF_CARE = {
  fever: [
    'Stay well-hydrated — water, broth, or electrolyte drinks throughout the day',
    'Acetaminophen or ibuprofen can help bring fever down and ease discomfort (follow label dosing)',
    'Rest as much as possible — your body is working hard',
    'Watch for rash, stiff neck, trouble breathing, or confusion and seek care immediately if any develop',
  ],
  cough_uri: [
    'Stay hydrated — warm liquids like tea or broth can soothe your throat and thin mucus',
    'Honey (for adults and children over 1) can ease cough as well as most OTC cough syrups',
    'Elevate your head at night to reduce post-nasal drip',
    'Seek care sooner if you develop a fever over 103°F, chest pain, or trouble breathing',
  ],
  sore_throat: [
    'Warm salt-water gargles (1/4 tsp salt in 8 oz warm water) several times a day',
    'Cold drinks, ice pops, or cold foods can numb the throat and reduce swelling',
    'OTC throat sprays or lozenges with benzocaine can help with pain',
    'If you develop high fever, swallowing difficulty, or a muffled voice — see a provider right away',
  ],
  ear_pain: [
    'A warm compress held gently against the ear can reduce pain',
    'OTC pain relievers like ibuprofen or acetaminophen can help with discomfort',
    'Sleep with the painful ear facing up to reduce pressure',
    'See a provider if you notice drainage from the ear, hearing changes, or fever',
  ],
  vomiting_diarrhea: [
    'Small sips of clear fluids frequently — water, broth, or electrolyte drinks like Pedialyte or Gatorade',
    'The BRAT diet (bananas, rice, applesauce, toast) is gentle on an upset stomach',
    'Avoid dairy, greasy foods, caffeine, and alcohol until symptoms resolve',
    'Seek care if you can\'t keep any fluids down for 8+ hours, see blood, or feel significantly worse',
  ],
  abdominal_pain: [
    'Rest and avoid heavy meals — stick to light, easily digestible foods',
    'A heating pad on low can help with cramping',
    'Avoid NSAIDs like ibuprofen for stomach pain — use acetaminophen if needed',
    'Seek care immediately if pain becomes severe, you develop fever, or can\'t pass gas',
  ],
  urinary_symptoms: [
    'Drink plenty of water — staying hydrated helps flush bacteria',
    'Avoid caffeine, alcohol, and spicy foods which can irritate the bladder',
    'Phenazopyridine (AZO) can temporarily relieve burning and urgency — available OTC',
    'Monitor closely — UTIs typically need antibiotics. If symptoms worsen or fever develops, see a provider today',
  ],
  rash_skin: [
    'Keep the affected area clean and dry',
    'Avoid scratching — cool compresses or calamine lotion can help with itch',
    'OTC hydrocortisone cream (1%) can reduce mild inflammation',
    'See a provider if the rash spreads rapidly, develops blisters, or is accompanied by fever',
  ],
  headache: [
    'Rest in a quiet, dark room if light or noise makes it worse',
    'OTC pain relievers (ibuprofen or acetaminophen) work best when taken early — don\'t wait until pain peaks',
    'Stay hydrated — dehydration is a common headache trigger',
    'Seek care immediately if your headache is the worst you\'ve ever had, or if you develop fever, stiff neck, or vision changes',
  ],
  minor_injury_sprain: [
    'RICE: Rest the injury, Ice for 20 minutes on / 20 off, Compression with a bandage, Elevation above heart level',
    'OTC anti-inflammatories like ibuprofen can help with swelling and pain',
    'Avoid heat, alcohol, running, and massage in the first 48–72 hours — they increase swelling',
    'If weight-bearing becomes painful, deformity appears, or swelling significantly worsens — get it checked',
  ],
  chest_discomfort: [
    'Rest and avoid physical exertion until you\'re evaluated',
    'Avoid caffeine and heavy meals',
    'Do not drive yourself if your symptoms get worse',
    'If chest pain worsens, spreads to your arm or jaw, or you become short of breath — call 911 immediately',
  ],
  shortness_of_breath: [
    'Sit upright — this position typically makes breathing easier than lying flat',
    'Avoid triggers: smoke, strong odors, cold air, or known allergens',
    'If you have a rescue inhaler and this is a known asthma pattern, use it as directed',
    'Call 911 if your breathing becomes significantly worse, you can\'t speak in full sentences, or your lips turn blue',
  ],
  dizziness: [
    'Change positions slowly — sit on the edge of the bed before standing',
    'Stay hydrated — dehydration is a frequent cause of dizziness',
    'Avoid driving or operating machinery until it fully resolves',
    'Seek care immediately if you experience sudden one-sided weakness, slurred speech, or severe headache',
  ],
  allergic_reaction: [
    'Take an OTC antihistamine (like Benadryl or Claritin) to reduce symptoms',
    'Avoid any known or suspected triggers',
    'Watch for worsening — hives spreading, throat tightness, difficulty breathing, or lightheadedness',
    'If symptoms escalate, use an EpiPen if available and call 911 immediately',
  ],
  constipation: [
    'Increase water intake significantly — aim for 8+ glasses a day',
    'Add fiber gradually: fruits, vegetables, beans, and whole grains',
    'Gentle movement like a short walk can stimulate bowel activity',
    'OTC options: Miralax (polyethylene glycol) is safe for most people and works gently over 1–3 days',
  ],
  joint_pain_swelling: [
    'RICE: Rest the joint, Ice 20 minutes on / 20 off, Compression, Elevation above heart level',
    'OTC anti-inflammatories like ibuprofen help with both pain and swelling',
    'Avoid putting full weight on the joint until evaluated',
    'See a provider today if the joint becomes hot, very swollen, or you develop fever',
  ],
  vaginal_symptoms: [
    'Avoid douching, scented soaps, or any irritating products in the vaginal area',
    'Wear loose, breathable cotton underwear',
    'Stay well-hydrated',
    'OTC yeast infection treatments (like Monistat) are appropriate if symptoms match — but see a provider if this is new or unclear',
  ],
  rectal_bleeding: [
    'Increase fiber and water intake to soften stools',
    'OTC hemorrhoid treatments (Preparation H, witch hazel pads) can reduce discomfort',
    'Avoid straining during bowel movements',
    'See a provider sooner if bleeding continues, worsens, or you notice dark/black stools',
  ],
  palpitations: [
    'Reduce caffeine, alcohol, and energy drinks — common triggers',
    'Practice slow, deep breathing when palpitations occur',
    'Avoid stimulants and track when they happen (activity, food, stress)',
    'Seek care immediately if palpitations return with chest pain, shortness of breath, or fainting',
  ],
  weakness_fatigue: [
    'Rest as much as your body asks for — this is your immune system working',
    'Stay hydrated and eat light, nourishing foods even if appetite is low',
    'Avoid strenuous activity until you feel significantly better',
    'Seek care if fatigue is severe, you can\'t get out of bed, or new symptoms develop',
  ],
};

const SELF_CARE_DEFAULTS = {
  home_care: [
    'Rest as much as your body asks for',
    'Stay hydrated — water and clear fluids throughout the day',
    'Monitor for worsening and don\'t hesitate to seek care if things change',
  ],
  care_soon: [
    'Rest and take it easy until your appointment',
    'Stay hydrated',
    'Seek care sooner if symptoms worsen significantly before your visit',
  ],
};

// ─── Safety-net copy ──────────────────────────────────────────────────────────

const SAFETY_NET = {
  care_today: 'If symptoms worsen significantly before you\'re seen, don\'t wait — go to the nearest ER or call 911.',
  care_soon: 'If your symptoms worsen or you develop fever, chest pain, or trouble breathing — seek care sooner.',
  home_care: 'If symptoms persist beyond 3–5 days, get worse, or any new concerning symptoms develop — see a provider.',
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Get all content needed for the result screen.
 *
 * @param {string} protocolId
 * @param {string} careLevel
 * @param {string[]} reasonCodes
 * @param {object} answers
 * @returns {{ explanation: string, selfCare: string[] | null, safetyNet: string | null }}
 */
export function getResultContent(protocolId, careLevel, reasonCodes = [], answers = {}) {
  // Explanation
  let explanation;
  try {
    const fn = EXPLANATION_FNS[protocolId];
    explanation = fn ? fn(careLevel, answers, reasonCodes) : (FALLBACK[careLevel] || FALLBACK.care_soon);
  } catch {
    explanation = FALLBACK[careLevel] || FALLBACK.care_soon;
  }

  // Self-care — only for home_care and care_soon
  let selfCare = null;
  if (careLevel === 'home_care' || careLevel === 'care_soon') {
    selfCare = SELF_CARE[protocolId] || SELF_CARE_DEFAULTS[careLevel] || null;
  }

  // Safety-net — not shown for emergency_now
  const safetyNet = careLevel !== 'emergency_now' ? (SAFETY_NET[careLevel] || null) : null;

  return { explanation, selfCare, safetyNet };
}
